import { createContext, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useAuth } from 'react-oidc-context';

import type { MeCompany } from '@/api/types';
import { useMe } from '@/features/me/MeContext';
import { getCompanyModules } from '@/features/settings/api';
import { isModuleEnabled, type ModuleKey } from '@/features/company/modules';
import { useAsync } from '@/lib/useAsync';

/**
 * The normal app always works within one **active company** (§ Bedrijfscontext).
 *
 * Source is `me.companies`; a company-admin who administers a company without
 * being a member of it (`permissions.administeredCompanyIds`) is not yet
 * resolvable to a name here — `/me` only returns memberships, not every
 * company an admin administers — so a caller that needs those too still has
 * to fetch `GET /companies` itself (open point, see this WP's report). One
 * company selects itself; more than one needs a choice, persisted per
 * account so switching accounts restores each one's own pick
 * (`localStorage.activeCompany:<sub>`).
 */
export type ActiveCompanyId = string;

export interface CompanyContextValue {
  /**
   * `null` only in the personal context: no company selected at all (zero
   * memberships, or a stored choice that no longer applies).
   */
  company: MeCompany | null;
  companies: readonly MeCompany[];
  /** Whether a picker is worth showing at all — more than one company to choose between. */
  hasChoice: boolean;
  setActive: (id: ActiveCompanyId) => void;
  /**
   * The active company's module toggles (PLAN-FASE3.md WP-00/WP-06), fetched
   * once per active company alongside `company` itself. `null` while there is
   * no active company or the fetch hasn't resolved yet — `isModuleEnabled`
   * treats that (and any module missing from the response) as enabled, so
   * nothing locks out before the real answer is in.
   */
  modules: Readonly<Record<string, boolean>> | null;
  /** Convenience wrapper around `isModuleEnabled(modules, key)`. */
  isModuleEnabled: (key: ModuleKey) => boolean;
}

// Exported (only) so test helpers can provide a `CompanyContextValue`
// directly without a live `useAuth()` — see `src/test/renderWithMe.tsx`.
// eslint-disable-next-line react-refresh/only-export-components -- test-only export, see above
export const CompanyContext = createContext<CompanyContextValue | null>(null);

function storageKey(sub: string): string {
  return `activeCompany:${sub}`;
}

function readStorage(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    // Private browsing / blocked storage: fall through to the default.
    return null;
  }
}

function writeStorage(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Best-effort only — an unpersisted active company is still a working one.
  }
}

function resolveActiveId(sub: string, companies: readonly MeCompany[]): ActiveCompanyId | null {
  const stored = readStorage(storageKey(sub));
  if (stored && companies.some((c) => c.id === stored)) {
    return stored;
  }
  // No (valid) stored choice: one company auto-selects, otherwise no default.
  const [only] = companies;
  return companies.length === 1 && only ? only.id : null;
}

/**
 * Provider for `useActiveCompany()`. Not yet mounted in `src/app/routes.tsx`
 * — that is WP-08's job, once the shell has somewhere to render a company
 * picker; this WP delivers the hook itself.
 */
export function CompanyProvider({ children }: { children: ReactNode }) {
  const { me } = useMe();
  const auth = useAuth();
  // Falls back to a fixed key rather than throwing: this provider is usable
  // in isolation (tests, Storybook-style rendering) without a live session.
  const sub = auth.user?.profile.sub ?? 'anonymous';
  const companies = useMemo(() => me.companies ?? [], [me.companies]);

  const [activeId, setActiveId] = useState<ActiveCompanyId | null>(() =>
    resolveActiveId(sub, companies),
  );
  // Tracks which account `activeId` was last resolved for, so an account
  // switch (`sub` changing) re-derives that account's own stored choice
  // during render — the React-recommended way to adjust state on a prop
  // change, instead of a `useEffect` that would render the old account's
  // choice for one frame first.
  const [resolvedFor, setResolvedFor] = useState(sub);
  if (sub !== resolvedFor) {
    setResolvedFor(sub);
    setActiveId(resolveActiveId(sub, companies));
  }

  const setActive = (id: ActiveCompanyId) => {
    setActiveId(id);
    writeStorage(storageKey(sub), id);
  };

  const company = useMemo<CompanyContextValue['company']>(() => {
    if (activeId) {
      return companies.find((c) => c.id === activeId) ?? null;
    }
    const [only] = companies;
    return companies.length === 1 && only ? only : null;
  }, [activeId, companies]);

  const companyId = company?.id ?? null;
  const modulesState = useAsync(
    () => (companyId ? getCompanyModules(companyId) : Promise.resolve(null)),
    [companyId],
  );

  const value: CompanyContextValue = {
    company,
    companies,
    hasChoice: companies.length > 1,
    setActive,
    modules: modulesState.data,
    isModuleEnabled: (key) => isModuleEnabled(modulesState.data, key),
  };

  return <CompanyContext.Provider value={value}>{children}</CompanyContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components -- the hook belongs with its provider
export function useActiveCompany(): CompanyContextValue {
  const value = useContext(CompanyContext);
  if (!value) {
    throw new Error('useActiveCompany() requires a CompanyProvider above it');
  }
  return value;
}
